import { SITE_DESCRIPTION, SITE_NAME, SITE_URL } from "./config";
import {
    breadcrumbSchema,
    faqPageSchema,
    mobileApplicationSchema,
    webPageSchema,
} from "./schemas";

function pageSchemas(title, path, description, extra = []) {
    const url = `${SITE_URL}${path}`;
    return [
        webPageSchema({ title, description, url }),
        breadcrumbSchema([
            { name: "Home", url: SITE_URL },
            { name: title, url },
        ]),
        ...extra,
    ];
}

export const PAGE_SEO = {
    "/": {
        title: `${SITE_NAME} | Save, Shop, Budget & Track Spending in One App`,
        description: SITE_DESCRIPTION,
        keywords:
            "Accruewise app, save money app India, budgeting app, virtual card, marketplace, spend tracker",
        schemas: [
            mobileApplicationSchema(),
            webPageSchema({
                title: `${SITE_NAME} | Save, Shop, Budget & Track Spending in One App`,
                description: SITE_DESCRIPTION,
                url: SITE_URL,
            }),
        ],
    },
    "/card": {
        title: "Accruewise Virtual Card | Rewards, Cashback & Flexible Payments",
        description:
            "Get the Accruewise virtual RuPay card for rewarding online and in-store payments, cashback, spending controls, and secure digital checkout.",
        keywords:
            "Accruewise card, virtual card India, RuPay card, cashback card, digital payments",
        schemas: pageSchemas(
            "Virtual Card",
            "/card",
            "Get the Accruewise virtual RuPay card for rewarding online and in-store payments, cashback, spending controls, and secure digital checkout.",
            [
                {
                    "@context": "https://schema.org",
                    "@type": "FinancialProduct",
                    name: "Accruewise Virtual Card",
                    provider: { "@type": "Organization", name: SITE_NAME },
                    description:
                        "A virtual RuPay card for flexible payments, cashback, and spend controls inside the Accruewise app.",
                },
            ]
        ),
    },
    "/marketplace": {
        title: "Accruewise Marketplace | Shop Smarter & Compare Deals In-App",
        description:
            "Shop smarter with the Accruewise marketplace. Compare prices, find deals, pay flexibly, track orders, and manage loyalty rewards from one app.",
        keywords:
            "Accruewise marketplace, shop smarter, price comparison, in-app shopping India",
        schemas: pageSchemas(
            "Marketplace",
            "/marketplace",
            "Shop smarter with the Accruewise marketplace. Compare prices, find deals, pay flexibly, track orders, and manage loyalty rewards from one app."
        ),
    },
    "/ai-agents": {
        title: "Accruewise AI Agents | Create Goals & Check Out by Voice",
        description:
            "Meet the Accruewise AI agents: say a sentence to create a savings goal with a real product and price, get a daily saving plan, and check out with agentic payments you approve.",
        keywords:
            "Accruewise AI agent, voice savings goal, agentic checkout, AI personal finance India",
        schemas: pageSchemas(
            "AI Agents",
            "/ai-agents",
            "Meet the Accruewise AI agents: say a sentence to create a savings goal with a real product and price, get a daily saving plan, and check out with agentic payments you approve."
        ),
    },
    "/about-us": {
        title: "About Accruewise | Making Money Management Empowering",
        description:
            "Learn about Accruewise, an India-first fintech company building tools for saving, budgeting, marketplace shopping, and smarter everyday money decisions.",
        keywords: "about Accruewise, fintech company India, Accruewise team",
        schemas: pageSchemas(
            "About Us",
            "/about-us",
            "Learn about Accruewise, an India-first fintech company building tools for saving, budgeting, marketplace shopping, and smarter everyday money decisions."
        ),
    },
    "/faqs": {
        title: "Accruewise FAQs | Answers About Saving, Payments & the App",
        description:
            "Find answers about Accruewise: what the app does, availability in India, deposits, withdrawals, security, and data privacy.",
        keywords: "Accruewise FAQ, Accruewise help, Accruewise support questions",
        schemas: [faqPageSchema(), ...pageSchemas(
            "FAQs",
            "/faqs",
            "Find answers about Accruewise: what the app does, availability in India, deposits, withdrawals, security, and data privacy."
        )],
    },
    "/contact-us": {
        title: "Contact Accruewise | Customer Support & Help",
        description:
            "Contact Accruewise for product support, account help, and business inquiries. Email care@accruewise.in or use the contact page.",
        keywords: "contact Accruewise, Accruewise support, customer care",
        schemas: pageSchemas(
            "Contact Us",
            "/contact-us",
            "Contact Accruewise for product support, account help, and business inquiries.",
            [
                {
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    name: "Contact Accruewise",
                    url: `${SITE_URL}/contact-us`,
                },
            ]
        ),
    },
    "/privacy-policy": {
        title: "Accruewise Privacy Policy",
        description:
            "Read the Accruewise privacy policy to understand how personal data is collected, used, stored, and protected on the website and mobile app.",
        schemas: pageSchemas(
            "Privacy Policy",
            "/privacy-policy",
            "Read the Accruewise privacy policy to understand how personal data is collected, used, stored, and protected."
        ),
    },
    "/terms-and-conditions": {
        title: "Accruewise Terms of Service",
        description:
            "Review Accruewise terms of service for website and app usage, account responsibilities, communications, and legal conditions.",
        schemas: pageSchemas(
            "Terms of Service",
            "/terms-and-conditions",
            "Review Accruewise terms of service for website and app usage, account responsibilities, and legal conditions."
        ),
    },
    "/refund-and-cancellation": {
        title: "Accruewise Refund & Cancellation Policy",
        description:
            "Read Accruewise refund and cancellation policy details for services, transactions, and customer grievance support.",
        schemas: pageSchemas(
            "Refund and Cancellation",
            "/refund-and-cancellation",
            "Read Accruewise refund and cancellation policy details for services, transactions, and customer grievance support."
        ),
    },
};

export const NOT_FOUND_SEO = {
    title: `Page Not Found | ${SITE_NAME}`,
    description: "The page you are looking for could not be found on Accruewise.",
    robots: "noindex, follow",
    schemas: [],
};

export function getPageSeo(pathname) {
    return PAGE_SEO[pathname] || NOT_FOUND_SEO;
}
